function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * @typedef {Object} LayeredSlideshowRefs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/**
 * @typedef {Object} DragState
 * @property {number} target
 * @property {number} start
 * @property {number} max
 * @property {number} activeSize - The resolved pixel size of the active panel at drag start
 * @property {boolean} left
 * @property {boolean} [dragging]
 * @property {boolean} [prevent]
 * @property {number} [progress]
 */

const DRAG_THRESHOLD = 5;
const MAX_DRAG_WIDTH_RATIO = 0.8;
const DRAG_COMPLETE_THRESHOLD = 0.5;
const INACTIVE_SIZE = 56; // Px size of inactive tabs on desktop
const INACTIVE_MOBILE_SIZE = 44; // Px size of inactive tabs on mobile
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** @extends {Component<LayeredSlideshowRefs>} */
var _active = _classPrivateFieldLooseKey("active");
var _drag = _classPrivateFieldLooseKey("drag");
var _abort = _classPrivateFieldLooseKey("abort");
var _isMobile = _classPrivateFieldLooseKey("isMobile");
var _heightObserver = _classPrivateFieldLooseKey("heightObserver");
var _contentObserver = _classPrivateFieldLooseKey("contentObserver");
var _containerObserver = _classPrivateFieldLooseKey("containerObserver");
var _inactiveSize = _classPrivateFieldLooseKey("inactiveSize");
var _bindLayeredVideoPosterReady = _classPrivateFieldLooseKey("bindLayeredVideoPosterReady");
var _setupEventListeners = _classPrivateFieldLooseKey("setupEventListeners");
var _handleKeydown = _classPrivateFieldLooseKey("handleKeydown");
var _handleTabClick = _classPrivateFieldLooseKey("handleTabClick");
var _handleTabFocus = _classPrivateFieldLooseKey("handleTabFocus");
var _setupPanelFocusManagement = _classPrivateFieldLooseKey("setupPanelFocusManagement");
var _handlePanelKeydown = _classPrivateFieldLooseKey("handlePanelKeydown");
var _focusPanelEdge = _classPrivateFieldLooseKey("focusPanelEdge");
var _getFocusableElements = _classPrivateFieldLooseKey("getFocusableElements");
var _preventClickDuringDrag = _classPrivateFieldLooseKey("preventClickDuringDrag");
var _handleMediaQueryChange = _classPrivateFieldLooseKey("handleMediaQueryChange");
var _activate = _classPrivateFieldLooseKey("activate");
var _updateActiveTab = _classPrivateFieldLooseKey("updateActiveTab");
var _updateGridSizes = _classPrivateFieldLooseKey("updateGridSizes");
var _startDrag = _classPrivateFieldLooseKey("startDrag");
var _initializeDrag = _classPrivateFieldLooseKey("initializeDrag");
var _handleDrag = _classPrivateFieldLooseKey("handleDrag");
var _endDrag = _classPrivateFieldLooseKey("endDrag");
var _observeContentHeight = _classPrivateFieldLooseKey("observeContentHeight");
var _syncHeight = _classPrivateFieldLooseKey("syncHeight");
var _syncDesktopHeight = _classPrivateFieldLooseKey("syncDesktopHeight");
var _syncMobileHeight = _classPrivateFieldLooseKey("syncMobileHeight");
var _clearHeightStyles = _classPrivateFieldLooseKey("clearHeightStyles");
var _getMaxContentHeight = _classPrivateFieldLooseKey("getMaxContentHeight");
export class LayeredSlideshowComponent extends Component {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _getMaxContentHeight, {
      value: _getMaxContentHeight2
    });
    Object.defineProperty(this, _clearHeightStyles, {
      value: _clearHeightStyles2
    });
    /**
     * @param {number} contentHeight
     * @param {boolean} isAuto
     */
    Object.defineProperty(this, _syncMobileHeight, {
      value: _syncMobileHeight2
    });
    /**
     * @param {number} contentHeight
     * @param {boolean} isAuto
     */
    Object.defineProperty(this, _syncDesktopHeight, {
      value: _syncDesktopHeight2
    });
    Object.defineProperty(this, _syncHeight, {
      value: _syncHeight2
    });
    Object.defineProperty(this, _observeContentHeight, {
      value: _observeContentHeight2
    });
    /**
     * @param {AbortController} ac
     */
    Object.defineProperty(this, _endDrag, {
      value: _endDrag2
    });
    /**
     * @param {PointerEvent} event
     */
    Object.defineProperty(this, _handleDrag, {
      value: _handleDrag2
    });
    /**
     * @param {PointerEvent} event
     * @param {number} [initialTarget]
     */
    Object.defineProperty(this, _initializeDrag, {
      value: _initializeDrag2
    });
    /**
     * @param {PointerEvent} event
     */
    Object.defineProperty(this, _startDrag, {
      value: _startDrag2
    });
    /**
     * @param {number} [containerSize] - Override container size (used during viewport switch)
     */
    Object.defineProperty(this, _updateGridSizes, {
      value: _updateGridSizes2
    });
    Object.defineProperty(this, _updateActiveTab, {
      value: _updateActiveTab2
    });
    /**
     * @param {number} index
     * @param {boolean} [instant]
     */
    Object.defineProperty(this, _activate, {
      value: _activate2
    });
    Object.defineProperty(this, _preventClickDuringDrag, {
      value: _preventClickDuringDrag2
    });
    /**
     * @param {HTMLElement} panel
     * @returns {HTMLElement[]}
     */
    Object.defineProperty(this, _getFocusableElements, {
      value: _getFocusableElements2
    });
    /**
     * @param {number} index
     * @param {'start' | 'end'} [position]
     */
    Object.defineProperty(this, _focusPanelEdge, {
      value: _focusPanelEdge2
    });
    /**
     * @param {KeyboardEvent} event
     * @param {number} index
     */
    Object.defineProperty(this, _handlePanelKeydown, {
      value: _handlePanelKeydown2
    });
    /**
     * @param {AddEventListenerOptions & { signal: AbortSignal }} opts
     */
    Object.defineProperty(this, _setupPanelFocusManagement, {
      value: _setupPanelFocusManagement2
    });
    Object.defineProperty(this, _handleTabFocus, {
      value: _handleTabFocus2
    });
    Object.defineProperty(this, _handleTabClick, {
      value: _handleTabClick2
    });
    Object.defineProperty(this, _handleKeydown, {
      value: _handleKeydown2
    });
    Object.defineProperty(this, _setupEventListeners, {
      value: _setupEventListeners2
    });
    /** Shopify video + üstteki poster img: aktif sekmede img erken kapanınca beyaz kutu oluşmasın */
    Object.defineProperty(this, _bindLayeredVideoPosterReady, {
      value: _bindLayeredVideoPosterReady2
    });
    /** @returns {number} The inactive tab size in pixels based on current viewport */
    Object.defineProperty(this, _inactiveSize, {
      get: _get_inactiveSize,
      set: void 0
    });
    this.requiredRefs = ['container'];
    Object.defineProperty(this, _active, {
      writable: true,
      value: 0
    });
    /** @type {DragState | null} */
    Object.defineProperty(this, _drag, {
      writable: true,
      value: null
    });
    /** @type {AbortController | null} */
    Object.defineProperty(this, _abort, {
      writable: true,
      value: null
    });
    Object.defineProperty(this, _isMobile, {
      writable: true,
      value: false
    });
    /** @type {ResizeObserver | null} */
    Object.defineProperty(this, _heightObserver, {
      writable: true,
      value: null
    });
    /** @type {MutationObserver | null} */
    Object.defineProperty(this, _contentObserver, {
      writable: true,
      value: null
    });
    /** @type {ResizeObserver | null} */
    Object.defineProperty(this, _containerObserver, {
      writable: true,
      value: null
    });
    Object.defineProperty(this, _handleMediaQueryChange, {
      writable: true,
      value: () => {
        const wasMobile = _classPrivateFieldLooseBase(this, _isMobile)[_isMobile];
        _classPrivateFieldLooseBase(this, _isMobile)[_isMobile] = isMobileBreakpoint();
        if (wasMobile !== _classPrivateFieldLooseBase(this, _isMobile)[_isMobile]) {
          const {
            container
          } = this.refs;
          container.setAttribute('data-instant-transitions', '');
          _classPrivateFieldLooseBase(this, _clearHeightStyles)[_clearHeightStyles]();
          // Re-calculate height first so grid calculation has correct container dimensions
          _classPrivateFieldLooseBase(this, _observeContentHeight)[_observeContentHeight]();
          _classPrivateFieldLooseBase(this, _updateActiveTab)[_updateActiveTab]();
          _classPrivateFieldLooseBase(this, _setupEventListeners)[_setupEventListeners]();
          requestAnimationFrame(() => {
            container.removeAttribute('data-instant-transitions');
          });
        }
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    const {
      tabs
    } = this.refs;
    if (!tabs?.length) return;
    _classPrivateFieldLooseBase(this, _active)[_active] = Math.max(0, tabs.findIndex(t => t.getAttribute('aria-selected') === 'true'));
    _classPrivateFieldLooseBase(this, _isMobile)[_isMobile] = isMobileBreakpoint();
    mediaQueryLarge.addEventListener('change', _classPrivateFieldLooseBase(this, _handleMediaQueryChange)[_handleMediaQueryChange]);
    _classPrivateFieldLooseBase(this, _containerObserver)[_containerObserver] = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          // Use contentBoxSize if available for better precision, or fallback to contentRect
          const boxSize = entry.contentBoxSize[0];
          const isMobile = _classPrivateFieldLooseBase(this, _isMobile)[_isMobile];
          let size;
          if (boxSize) {
            size = isMobile ? boxSize.blockSize : boxSize.inlineSize;
          } else {
            size = isMobile ? entry.contentRect.height : entry.contentRect.width;
          }
          _classPrivateFieldLooseBase(this, _updateGridSizes)[_updateGridSizes](size);
        }
      }
    });
    _classPrivateFieldLooseBase(this, _containerObserver)[_containerObserver].observe(this.refs.container);
    _classPrivateFieldLooseBase(this, _updateActiveTab)[_updateActiveTab]();
    _classPrivateFieldLooseBase(this, _setupEventListeners)[_setupEventListeners]();
    _classPrivateFieldLooseBase(this, _observeContentHeight)[_observeContentHeight]();
    _classPrivateFieldLooseBase(this, _bindLayeredVideoPosterReady)[_bindLayeredVideoPosterReady]();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _abort)[_abort]?.abort();
    _classPrivateFieldLooseBase(this, _heightObserver)[_heightObserver]?.disconnect();
    _classPrivateFieldLooseBase(this, _heightObserver)[_heightObserver] = null;
    _classPrivateFieldLooseBase(this, _contentObserver)[_contentObserver]?.disconnect();
    _classPrivateFieldLooseBase(this, _contentObserver)[_contentObserver] = null;
    _classPrivateFieldLooseBase(this, _containerObserver)[_containerObserver]?.disconnect();
    _classPrivateFieldLooseBase(this, _containerObserver)[_containerObserver] = null;
    mediaQueryLarge.removeEventListener('change', _classPrivateFieldLooseBase(this, _handleMediaQueryChange)[_handleMediaQueryChange]);
  }

  /**
   * Public method to select a slide by index
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, {
    instant = false
  } = {}) {
    _classPrivateFieldLooseBase(this, _activate)[_activate](index, instant);
  }
}
function _get_inactiveSize() {
  return _classPrivateFieldLooseBase(this, _isMobile)[_isMobile] ? INACTIVE_MOBILE_SIZE : INACTIVE_SIZE;
}
function _bindLayeredVideoPosterReady2() {
  for (const panel of this.refs.panels ?? []) {
    const video = panel.querySelector('video.layered-slideshow__video');
    if (!video || !(video instanceof HTMLVideoElement)) continue;
    if (video.dataset.layeredPosterReadyBound === 'true') continue;
    video.dataset.layeredPosterReadyBound = 'true';
    const mark = () => {
      video.classList.add('is-layered-video-ready');
    };
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      mark();
    } else {
      video.addEventListener('loadeddata', mark, {
        once: true
      });
    }
  }
}
function _setupEventListeners2() {
  _classPrivateFieldLooseBase(this, _abort)[_abort]?.abort();
  _classPrivateFieldLooseBase(this, _abort)[_abort] = new AbortController();
  const opts = {
    signal: _classPrivateFieldLooseBase(this, _abort)[_abort].signal
  };
  const {
    container,
    tabs
  } = this.refs;
  this.addEventListener('keydown', e => _classPrivateFieldLooseBase(this, _handleKeydown)[_handleKeydown](e), opts);
  for (const [i, tab] of tabs.entries()) {
    tab.addEventListener('click', e => _classPrivateFieldLooseBase(this, _handleTabClick)[_handleTabClick](e, i), opts);
    tab.addEventListener('focus', e => _classPrivateFieldLooseBase(this, _handleTabFocus)[_handleTabFocus](e, i), opts);
  }
  _classPrivateFieldLooseBase(this, _setupPanelFocusManagement)[_setupPanelFocusManagement](opts);
  if (!_classPrivateFieldLooseBase(this, _isMobile)[_isMobile]) {
    container.addEventListener('pointerdown', e => _classPrivateFieldLooseBase(this, _startDrag)[_startDrag](e), opts);
    container.addEventListener('click', e => _classPrivateFieldLooseBase(this, _preventClickDuringDrag)[_preventClickDuringDrag](e), {
      ...opts,
      capture: true
    });
  }
}
function _handleKeydown2(/** @type {KeyboardEvent} */e) {
  const target = /** @type {HTMLElement} */e.target;
  if (target.getAttribute('role') !== 'tab') return;
  const {
    tabs
  } = this.refs;
  if (!tabs) return;
  const i = tabs.indexOf(target);
  const navMap = {
    [_classPrivateFieldLooseBase(this, _isMobile)[_isMobile] ? 'ArrowUp' : 'ArrowLeft']: -1,
    [_classPrivateFieldLooseBase(this, _isMobile)[_isMobile] ? 'ArrowDown' : 'ArrowRight']: 1,
    Home: -i,
    End: tabs.length - 1 - i
  };
  const offset = navMap[e.key];
  if (offset !== undefined) {
    e.preventDefault();
    const nextIndex = (i + offset + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    _classPrivateFieldLooseBase(this, _activate)[_activate](nextIndex);
  }
}
function _handleTabClick2(/** @type {MouseEvent} */e, /** @type {number} */index) {
  e.preventDefault();
  _classPrivateFieldLooseBase(this, _activate)[_activate](index);
}
function _handleTabFocus2(/** @type {FocusEvent} */e, /** @type {number} */index) {
  const target = /** @type {HTMLElement} */e.target;
  if (target.matches(':focus-visible')) {
    _classPrivateFieldLooseBase(this, _activate)[_activate](index);
  }
}
function _setupPanelFocusManagement2(opts) {
  const {
    panels
  } = this.refs;
  if (!panels) return;
  for (const [index, panel] of panels.entries()) {
    panel.addEventListener('keydown', event => _classPrivateFieldLooseBase(this, _handlePanelKeydown)[_handlePanelKeydown](event, index), opts);
  }
}
function _handlePanelKeydown2(event, index) {
  if (event.key !== 'Tab') return;
  const {
    panels
  } = this.refs;
  const panel = /** @type {HTMLElement} */event.currentTarget;
  const focusable = _classPrivateFieldLooseBase(this, _getFocusableElements)[_getFocusableElements](panel);
  const firstFocusable = focusable[0];
  const lastFocusable = focusable[focusable.length - 1];
  if (event.shiftKey) {
    const isAtStart = firstFocusable && document.activeElement === firstFocusable || !focusable.length && document.activeElement === panel;
    if (isAtStart && index > 0) {
      event.preventDefault();
      _classPrivateFieldLooseBase(this, _activate)[_activate](index - 1);
      _classPrivateFieldLooseBase(this, _focusPanelEdge)[_focusPanelEdge](index - 1, 'end');
    }
    return;
  }
  const isAtEnd = lastFocusable && document.activeElement === lastFocusable || !focusable.length && document.activeElement === panel;
  if (isAtEnd && panels && index < panels.length - 1) {
    event.preventDefault();
    _classPrivateFieldLooseBase(this, _activate)[_activate](index + 1);
    _classPrivateFieldLooseBase(this, _focusPanelEdge)[_focusPanelEdge](index + 1, 'start');
  }
}
function _focusPanelEdge2(index, position = 'start') {
  const panel = this.refs.panels?.[index];
  if (!panel) return;
  const focusable = _classPrivateFieldLooseBase(this, _getFocusableElements)[_getFocusableElements](panel);
  const target = position === 'end' ? focusable[focusable.length - 1] : focusable[0];
  requestAnimationFrame(() => (target ?? panel).focus());
}
function _getFocusableElements2(panel) {
  return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el => !el.closest('[inert]')).map(el => (/** @type {HTMLElement} */el));
}
function _preventClickDuringDrag2(/** @type {MouseEvent} */e) {
  const target = /** @type {HTMLElement} */e.target;
  if (_classPrivateFieldLooseBase(this, _drag)[_drag]?.prevent && target.closest('[role="tab"]')) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}
function _activate2(index, instant = false) {
  const {
    container,
    tabs
  } = this.refs;
  if (!tabs || index === _classPrivateFieldLooseBase(this, _active)[_active] || index < 0 || index >= tabs.length) return;
  if (instant) {
    container.setAttribute('data-instant-transitions', '');
  }
  _classPrivateFieldLooseBase(this, _active)[_active] = index;
  _classPrivateFieldLooseBase(this, _updateActiveTab)[_updateActiveTab]();
  if (instant) {
    // Double rAF to ensure layout is fully settled before re-enabling transitions
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.removeAttribute('data-instant-transitions');
      });
    });
  }
}
function _updateActiveTab2() {
  const {
    tabs,
    panels
  } = this.refs;
  for (const [i, tab] of tabs?.entries() ?? []) {
    const isActive = i === _classPrivateFieldLooseBase(this, _active)[_active];
    tab.setAttribute('aria-selected', String(isActive));
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  }
  for (const [i, panel] of panels?.entries() ?? []) {
    const isActive = i === _classPrivateFieldLooseBase(this, _active)[_active];
    panel.toggleAttribute('inert', !isActive);
    panel.setAttribute('tabindex', isActive ? '0' : '-1');
    const video = panel.querySelector('video');
    if (video) {
      isActive ? video.play() : video.pause();
    }
  }
  _classPrivateFieldLooseBase(this, _updateGridSizes)[_updateGridSizes]();
}
function _updateGridSizes2(containerSize) {
  const {
    container,
    tabs
  } = this.refs;
  if (!tabs) return;
  const inactiveSize = _classPrivateFieldLooseBase(this, _inactiveSize)[_inactiveSize];
  const size = containerSize ?? (_classPrivateFieldLooseBase(this, _isMobile)[_isMobile] ? container.getBoundingClientRect().height : container.getBoundingClientRect().width);
  const activeSize = size - inactiveSize * (tabs.length - 1);
  const sizes = tabs.map((_, i) => i === _classPrivateFieldLooseBase(this, _active)[_active] ? `${activeSize}px` : `${inactiveSize}px`);
  container.style.setProperty('--active-tab', sizes.join(' '));
}
function _startDrag2(event) {
  if (_classPrivateFieldLooseBase(this, _isMobile)[_isMobile]) return;
  const {
    tabs
  } = this.refs;
  if (!tabs) return;
  const eventTarget = /** @type {HTMLElement} */event.target;
  const tab = eventTarget.closest('[role="tab"]');
  if (tab) {
    const i = tabs.indexOf(/** @type {HTMLElement} */tab);
    if (i === _classPrivateFieldLooseBase(this, _active)[_active]) return;
    const target = i > _classPrivateFieldLooseBase(this, _active)[_active] ? i + 1 : i;
    if (target >= tabs.length) return;
    _classPrivateFieldLooseBase(this, _initializeDrag)[_initializeDrag](event, target);
  } else {
    _classPrivateFieldLooseBase(this, _initializeDrag)[_initializeDrag](event);
  }
}
function _initializeDrag2(event, initialTarget) {
  const {
    container,
    tabs
  } = this.refs;
  if (!tabs) return;

  // Calculate active size from container dimensions
  const containerWidth = container.getBoundingClientRect().width;
  const inactiveSize = _classPrivateFieldLooseBase(this, _inactiveSize)[_inactiveSize];
  const activeSize = containerWidth - inactiveSize * (tabs.length - 1);
  _classPrivateFieldLooseBase(this, _drag)[_drag] = {
    target: initialTarget ?? -1,
    start: event.clientX,
    max: containerWidth * MAX_DRAG_WIDTH_RATIO,
    activeSize,
    left: initialTarget !== undefined ? initialTarget > _classPrivateFieldLooseBase(this, _active)[_active] : false
  };
  const ac = new AbortController();
  const opts = {
    signal: ac.signal
  };
  document.addEventListener('pointermove', e => _classPrivateFieldLooseBase(this, _handleDrag)[_handleDrag](e), opts);
  document.addEventListener('pointerup', () => _classPrivateFieldLooseBase(this, _endDrag)[_endDrag](ac), opts);
  document.addEventListener('pointercancel', () => _classPrivateFieldLooseBase(this, _endDrag)[_endDrag](ac), opts);
  event.preventDefault();
}
function _handleDrag2(event) {
  if (!_classPrivateFieldLooseBase(this, _drag)[_drag]) return;
  const {
    container,
    tabs
  } = this.refs;
  if (!container || !tabs) return;
  const delta = event.clientX - _classPrivateFieldLooseBase(this, _drag)[_drag].start;
  const move = Math.abs(delta);
  if (!_classPrivateFieldLooseBase(this, _drag)[_drag].dragging && move >= DRAG_THRESHOLD) {
    if (_classPrivateFieldLooseBase(this, _drag)[_drag].target === -1) {
      if (delta > 0 && _classPrivateFieldLooseBase(this, _active)[_active] > 0) {
        _classPrivateFieldLooseBase(this, _drag)[_drag].target = _classPrivateFieldLooseBase(this, _active)[_active] - 1;
        _classPrivateFieldLooseBase(this, _drag)[_drag].left = false;
      } else if (delta < 0 && _classPrivateFieldLooseBase(this, _active)[_active] < tabs.length - 1) {
        _classPrivateFieldLooseBase(this, _drag)[_drag].target = _classPrivateFieldLooseBase(this, _active)[_active] + 1;
        _classPrivateFieldLooseBase(this, _drag)[_drag].left = true;
      } else {
        return;
      }
    }
    _classPrivateFieldLooseBase(this, _drag)[_drag].dragging = true;
    container.setAttribute('data-dragging', '');
  }
  if (!_classPrivateFieldLooseBase(this, _drag)[_drag].dragging) return;
  const correct = _classPrivateFieldLooseBase(this, _drag)[_drag].left ? delta < 0 : delta > 0;
  const progress = correct ? Math.min(move / _classPrivateFieldLooseBase(this, _drag)[_drag].max, 1) : 0;
  const inactiveSize = _classPrivateFieldLooseBase(this, _inactiveSize)[_inactiveSize];
  const activeSize = _classPrivateFieldLooseBase(this, _drag)[_drag].activeSize;
  const range = activeSize - inactiveSize;
  const sizes = tabs.map((_, i) => {
    if (i === _classPrivateFieldLooseBase(this, _active)[_active]) {
      const active = Math.max(inactiveSize, activeSize - range * progress);
      return `${active}px`;
    }
    if (i === _classPrivateFieldLooseBase(this, _drag)[_drag]?.target) {
      const drag = inactiveSize + range * progress;
      return `${drag}px`;
    }
    return `${inactiveSize}px`;
  });
  container.style.setProperty('--active-tab', sizes.join(' '));
  _classPrivateFieldLooseBase(this, _drag)[_drag].progress = progress;
}
function _endDrag2(ac) {
  if (!_classPrivateFieldLooseBase(this, _drag)[_drag]) return;
  const {
    container
  } = this.refs;
  container?.removeAttribute('data-dragging');
  if (_classPrivateFieldLooseBase(this, _drag)[_drag].dragging) {
    _classPrivateFieldLooseBase(this, _drag)[_drag].prevent = true;
    setTimeout(() => _classPrivateFieldLooseBase(this, _drag)[_drag] = null, 100);
    if (_classPrivateFieldLooseBase(this, _drag)[_drag].progress && _classPrivateFieldLooseBase(this, _drag)[_drag].progress >= DRAG_COMPLETE_THRESHOLD) {
      _classPrivateFieldLooseBase(this, _activate)[_activate](_classPrivateFieldLooseBase(this, _drag)[_drag].target);
    } else {
      _classPrivateFieldLooseBase(this, _updateActiveTab)[_updateActiveTab]();
    }
  } else {
    _classPrivateFieldLooseBase(this, _drag)[_drag] = null;
  }
  ac.abort();
}
function _observeContentHeight2() {
  const {
    panels
  } = this.refs;
  _classPrivateFieldLooseBase(this, _heightObserver)[_heightObserver]?.disconnect();
  _classPrivateFieldLooseBase(this, _heightObserver)[_heightObserver] = new ResizeObserver(() => _classPrivateFieldLooseBase(this, _syncHeight)[_syncHeight]());
  _classPrivateFieldLooseBase(this, _contentObserver)[_contentObserver]?.disconnect();
  _classPrivateFieldLooseBase(this, _contentObserver)[_contentObserver] = new MutationObserver(() => _classPrivateFieldLooseBase(this, _syncHeight)[_syncHeight]());
  for (const panel of panels ?? []) {
    const content = panel.querySelector('.layered-slideshow__content');
    const inner = content?.querySelector('.group-block-content');

    // Observe all relevant elements for resize
    if (inner) _classPrivateFieldLooseBase(this, _heightObserver)[_heightObserver].observe(inner);
    if (content) _classPrivateFieldLooseBase(this, _heightObserver)[_heightObserver].observe(content);

    // Observe content for DOM mutations (new blocks, text changes)
    const target = inner ?? content;
    if (target) {
      _classPrivateFieldLooseBase(this, _contentObserver)[_contentObserver].observe(target, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }
  _classPrivateFieldLooseBase(this, _syncHeight)[_syncHeight]();
}
function _syncHeight2() {
  const {
    container
  } = this.refs;
  const contentHeight = _classPrivateFieldLooseBase(this, _getMaxContentHeight)[_getMaxContentHeight]();
  const isAuto = container.getAttribute('size') === 'auto';
  if (_classPrivateFieldLooseBase(this, _isMobile)[_isMobile]) {
    _classPrivateFieldLooseBase(this, _syncMobileHeight)[_syncMobileHeight](contentHeight, isAuto);
  } else {
    _classPrivateFieldLooseBase(this, _syncDesktopHeight)[_syncDesktopHeight](contentHeight, isAuto);
  }
}
function _syncDesktopHeight2(contentHeight, isAuto) {
  const {
    container
  } = this.refs;
  if (isAuto) {
    // Auto mode: fit to content height
    let minHeightTemp = Math.max(contentHeight, 150);
    container.style.height = `${minHeightTemp}px`;
    this.style.minHeight = `${minHeightTemp}px`;
  } else {
    // Temporarily clear inline style to measure CSS-defined min-height
    const savedMinHeight = this.style.minHeight;
    this.style.minHeight = '';
    const cssMinHeight = parseFloat(getComputedStyle(this).minHeight) || 0;
    this.style.minHeight = savedMinHeight;

    // Only set inline heights when content exceeds CSS min-height
    if (contentHeight > cssMinHeight) {
      this.style.minHeight = `${contentHeight}px`;
      container.style.height = `${contentHeight}px`;
    } else {
      this.style.minHeight = '';
      container.style.height = '';
    }
  }
}
function _syncMobileHeight2(contentHeight, isAuto) {
  const {
    container,
    tabs
  } = this.refs;
  if (!container || !tabs) return;
  const containerStyles = getComputedStyle(container);
  const inactiveStackHeight = (tabs.length - 1) * _classPrivateFieldLooseBase(this, _inactiveSize)[_inactiveSize];
  let minPanelHeight;
  if (isAuto) {
    // Auto mode: fit to content height with reasonable minimum
    minPanelHeight = 150;
  } else {
    // CSS variable is set on component, try reading from container (inherited) or component directly
    const inheritedValue = containerStyles.getPropertyValue('--layered-panel-height-mobile');
    const componentValue = getComputedStyle(this).getPropertyValue('--layered-panel-height-mobile');
    minPanelHeight = parseFloat(inheritedValue || componentValue) || 260;
  }
  const requiredActiveHeight = Math.max(minPanelHeight, contentHeight);
  container.style.setProperty('--active-panel-height', `${requiredActiveHeight}px`);
  container.style.height = `${requiredActiveHeight + inactiveStackHeight}px`;
}
function _clearHeightStyles2() {
  const {
    container
  } = this.refs;
  this.style.minHeight = '';
  container.style.height = '';
  container.style.removeProperty('--active-panel-height');
}
function _getMaxContentHeight2() {
  const {
    panels
  } = this.refs;
  let max = 0;
  for (const panel of panels ?? []) {
    const content = panel.querySelector('.layered-slideshow__content');
    if (!content) continue;
    const inner = /** @type {HTMLElement} */content.querySelector('.group-block-content') ?? content;

    // Temporarily set height to auto for accurate measurement
    // This is needed because height: 100% collapses when parent has no height
    const savedHeight = inner.style.height;
    inner.style.height = 'auto';
    const styles = getComputedStyle(content);
    const paddingTop = parseFloat(styles.paddingBlockStart || styles.paddingTop) || 0;
    const paddingBottom = parseFloat(styles.paddingBlockEnd || styles.paddingBottom) || 0;
    const height = (inner.scrollHeight || 0) + paddingTop + paddingBottom;
    if (height > max) max = height;

    // Restore original height
    inner.style.height = savedHeight;
  }
  return max;
}
customElements.define('layered-slideshow-component', LayeredSlideshowComponent);
